import {
  fetchPaymentRequests,
  getPaymentRequests,
  useBranches,
  useUsers,
  invalidatePaymentRequests,
} from "@api";
import { PaymentRequestDetails, paymentRequestStatusColors } from "@areas";
import PlanDetailModal from "@areas/calendar/displays/PlanDetailModal";
import { ServerTable, Badge, PageHeader, PageTitle } from "@components";
import { useTableFilters } from "@components/tables/shared";
import { useTableQuery } from "@hooks";
import { useQueryClient } from "@tanstack/react-query";
import { formatAmount, formatDateTime } from "@utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { readCustomer } from "@auth";
import { buildApiQuery } from "@utils";
import { usePaymentRequestTemplatesContext } from "@context/PaymentRequestTemplatesContext";

const Collections = () => {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("requestedOn");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");

  const [paymentRequestId, setPaymentRequestId] = useState(null);
  const [planActionId, setPlanActionId] = useState(null);
  const { data: branches = [] } = useBranches();
  const { data: users = [] } = useUsers();
  const { getTemplateNameById } = usePaymentRequestTemplatesContext();
  const queryClient = useQueryClient();

  // Memoize branches and users data to prevent race conditions
  const memoizedData = useMemo(() => {
    return {
      branches: branches.map((branch) => branch.name) || [],
      users: users.map((user) => user.name) || [],
    };
  }, [branches, users]);

  const tableConfig = useMemo(
    () => ({
      key: "collections",
      onClick: (x) => setPaymentRequestId(x.id),
      colCustomisation: true,
      columns: [
        {
          title: "Request Date",
          key: "requestedOn",
          id: "requestedOn",
          width: "150px",
          render: (x) => formatDateTime(x.requestedOn),
          sortFunc: true,
          type: "date",
        },
        {
          title: "Customer",
          key: "customerName",
          id: "customerName",
          width: "200px",
          render: (x) => x.customerName,
          sortFunc: true,
          type: "string",
        },
        {
          title: "Reference",
          key: "reference",
          id: "reference",
          width: "150px",
          render: (x) => x.reference,
          sortFunc: true,
          type: "string",
        },
        {
          title: "Amount",
          key: "amountRequested",
          id: "amountRequested",
          width: "170px",
          render: (x) => formatAmount(x.amountPaid || x.amountRequested),
          sortFunc: true,
          type: "number",
          filterPrefix: "£",
          right: true,
        },
        {
          title: "Invoices",
          key: "invoicesRequested",
          id: "invoicesRequested",
          width: "170px",
          render: (x) => x.invoicesRequested,
          type: "enum",
          permission: readCustomer,
          values: ["All", "Overdue", "Overdue + due this month", "Any"],
        },
        {
          title: "Last message sent",
          key: "messageTemplateName",
          id: "messageTemplateName",
          width: "200px",
          render: (x) => {
            const name =
              x.messageTemplateName === "[object Object]" //TODO: Sort template id in backend
                ? null
                : x.messageTemplateName;
            return (
              name ??
              (x.paymentRequestTemplateId
                ? (getTemplateNameById(x.paymentRequestTemplateId) ?? null)
                : null)
            );
          },
          type: "enum",
        },
        {
          title: "Requested by",
          key: "requestedByName",
          id: "requestedByName",
          width: "150px",
          render: (x) => x.requestedByName,
          sortFunc: true,
          type: "enum",
          values: memoizedData.users,
        },
        {
          title: "Branch",
          key: "branch",
          id: "branch",
          width: "120px",
          render: (x) => x.branch,
          sortFunc: true,
          type: "enum",
          values: memoizedData.branches,
        },
        {
          title: "Status",
          key: "status",
          id: "status",
          width: "110px",
          render: (x) => (
            <Badge color={paymentRequestStatusColors[x.status]}>
              {x.status}
            </Badge>
          ),
          type: "enum",
          values: [
            "Created",
            "Sent",
            "Delivered",
            "Opened",
            "Reminder sent",
            "Attempt failed",
            "PaymentProcessing",
            "Expired",
            "Paid",
            "Refunded",
            "Part-refund",
            "Delivery failed",
          ],
        },
      ],
    }),
    [memoizedData, readCustomer, getTemplateNameById],
  );

  const filterManager = useTableFilters("collections", tableConfig.columns);
  const { filters } = filterManager;

  const getQueryParams = useCallback(
    () => ({
      page,
      pageSize: 100,
      filters,
      sort,
      sortDirection,
      searchTerm,
      customerRequestsOnly: true,
    }),
    [page, filters, sort, sortDirection, searchTerm],
  );

  const query = useTableQuery({
    resource: "paymentRequests",
    fetcher: fetchPaymentRequests,
    getQueryParams,
  });

  // Reset to first page when the result-set scope changes.
  useEffect(() => {
    setPage(1);
  }, [filters, sort, sortDirection, searchTerm]);

  const reloadData = () => invalidatePaymentRequests(queryClient);

  const exportData = async () => {
    const query = buildApiQuery({
      page,
      pageSize: null,
      filters,
      sort,
      sortDirection,
      searchTerm,
      customerRequestsOnly: true,
    });
    const response = await getPaymentRequests(query);
    return response.data;
  };

  const handleSort = (key) => {
    if (sort === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setSortDirection("desc");
    }
  };

  const statistics = useMemo(
    () => [
      {
        label: "All",
        value: query.data ? query.data.statistics.all : null,
      },
      {
        label: "Paid",
        value: formatAmount(
          query.data ? query.data.statistics.paid : null,
        ),
      },
      {
        label: "Unpaid",
        value: formatAmount(
          query.data ? query.data.statistics.unpaid : null,
        ),
      },
    ],
    [query.data],
  );

  return (
    <>
      <PageHeader>
        <PageTitle>Collections</PageTitle>
      </PageHeader>
      <ServerTable
        config={tableConfig}
        data={query.data?.data || []}
        loading={query.isLoading || query.isPlaceholderData}
        statistics={statistics}
        results={query.data?.rows || 0}
        page={page}
        setPage={setPage}
        pageSize={100}
        filterManager={filterManager}
        sort={sort}
        sortDirection={sortDirection}
        onSort={handleSort}
        exportData={exportData}
        searchTerm={searchTerm}
        onSearchTermChange={(e) => setSearchTerm(e.target.value)}
      />
      <PaymentRequestDetails
        onClose={() => setPaymentRequestId(null)}
        onChange={() => reloadData()}
        paymentRequestId={paymentRequestId}
        onViewPlan={setPlanActionId}
      />
      <PlanDetailModal
        actionId={planActionId}
        isOpen={!!planActionId}
        onClose={() => setPlanActionId(null)}
        elevated
      />
    </>
  );
};

export default Collections;
